import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { userService } from '../../services/api';
import { useTranslation } from 'react-i18next';

// Styled components
const FormWrapper = styled.form`
  max-width: 700px;
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[4]};

  & > * {
    flex: 1 1 250px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.dark};
`;

const Input = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: ${({ theme }) => theme.transitions.default};

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
    box-shadow: var(--focus-act);
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.light};
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.medium};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 576px) {
    & > * {
      width: 100%;
    }
  }
`;

const AlertBox = styled.div`
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: ${({ variant }) => (variant === 'error' ? '#F8D7DA' : '#D4EDDA')};
  color: ${({ theme, variant }) =>
    variant === 'error' ? theme.colors.error : theme.colors.success};
`;

const Company = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    companyName: '',
    cvr: '',
    industry: '',
    address: '',
    phoneNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    invoiceEmail: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const applyProfile = (profile) => {
    setForm({
      companyName: profile?.companyName || '',
      cvr: profile?.cvr || '',
      industry: profile?.industry || '',
      address: profile?.address || '',
      phoneNumber: profile?.phoneNumber || '',
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
      invoiceEmail: profile?.invoiceEmail || ''
    });
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(null);
        const profile = await userService.getCustomerProfile();
        applyProfile(profile);
      } catch (err) {
        setError(t('customer.company.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.companyName.trim()) {
      setError(t('customer.company.companyNameRequired'));
      setSuccess(null);
      return;
    }

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError(t('customer.company.contactNameRequired'));
      setSuccess(null);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updated = await userService.updateCustomerProfile({
        companyName: form.companyName.trim(),
        industry: form.industry.trim(),
        address: form.address.trim(),
        phoneNumber: form.phoneNumber.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        // Billing email is editable; CVR is the business id and stays read-only,
        // so it is intentionally NOT part of the update payload.
        invoiceEmail: form.invoiceEmail.trim()
      });

      if (updated) {
        applyProfile(updated);
      }
      setSuccess(t('customer.company.updateSuccess'));
    } catch (err) {
      setError(t('customer.company.updateError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={t('customer.company.title')}
      subtitle={t('customer.company.subtitle')}
      padding="md"
    >
      {error && <AlertBox variant="error">{error}</AlertBox>}
      {success && <AlertBox variant="success">{success}</AlertBox>}

      {loading ? (
        <div>{t('customer.company.loading')}</div>
      ) : (
        <FormWrapper onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="companyName">{t('customer.company.companyName')}</Label>
            <Input
              id="companyName"
              name="companyName"
              type="text"
              value={form.companyName}
              onChange={handleChange}
              placeholder={t('customer.company.companyNamePlaceholder')}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="cvr">{t('customer.company.cvr')}</Label>
            <Input
              id="cvr"
              name="cvr"
              type="text"
              value={form.cvr}
              disabled
            />
            <HelpText>{t('customer.company.cvrReadonlyHelp')}</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="industry">{t('customer.company.industry')}</Label>
            <Input
              id="industry"
              name="industry"
              type="text"
              value={form.industry}
              onChange={handleChange}
              placeholder={t('customer.company.industryPlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="address">{t('customer.company.address')}</Label>
            <Input
              id="address"
              name="address"
              type="text"
              value={form.address}
              onChange={handleChange}
              placeholder={t('customer.company.addressPlaceholder')}
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phoneNumber">{t('customer.company.phoneNumber')}</Label>
            <Input
              id="phoneNumber"
              name="phoneNumber"
              type="tel"
              value={form.phoneNumber}
              onChange={handleChange}
              placeholder={t('customer.company.phoneNumberPlaceholder')}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label htmlFor="firstName">{t('customer.company.contactFirstName')}</Label>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                value={form.firstName}
                onChange={handleChange}
                placeholder={t('customer.company.firstNamePlaceholder')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="lastName">{t('customer.company.contactLastName')}</Label>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                value={form.lastName}
                onChange={handleChange}
                placeholder={t('customer.company.lastNamePlaceholder')}
                required
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="email">{t('customer.company.email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={form.email}
              disabled
            />
            <HelpText>{t('customer.company.emailHelp')}</HelpText>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="invoiceEmail">{t('customer.company.invoiceEmail')}</Label>
            <Input
              id="invoiceEmail"
              name="invoiceEmail"
              type="email"
              value={form.invoiceEmail}
              onChange={handleChange}
              placeholder={t('customer.company.invoiceEmailPlaceholder')}
            />
            <HelpText>{t('customer.company.invoiceEmailHelp')}</HelpText>
          </FormGroup>

          <FormActions>
            <Button type="submit" disabled={saving}>
              {saving ? t('common.saving') : t('customer.company.saveChanges')}
            </Button>
          </FormActions>
        </FormWrapper>
      )}
    </Card>
  );
};

export default Company;
